import React, { useState } from "react";
import { Plus, X, Star, Trash2 } from "lucide-react";

import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import {
  createSavedFilter,
  deleteSavedFilter,
  updateSavedFilter,
} from "../api/savedFiltersAPI";
import { labelForStatus } from "../constants/householdStatus";

const SavedFiltersPanel = ({
  projectId,
  project,
  views,
  setViews,
  loading,
  getCurrentFilters,
  onApplyView,
  activeViewId,
}) => {
  const { toast } = useToast();
  const confirm = useConfirm();

  const [showForm, setShowForm] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [newViewIsDefault, setNewViewIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSaveCurrent = async () => {
    const name = newViewName.trim();
    if (!name) {
      toast.warning("Please give this view a name.");
      return;
    }
    setSubmitting(true);
    try {
      const filters = getCurrentFilters();
      const created = await createSavedFilter(projectId, {
        name,
        filters,
        is_default: newViewIsDefault,
      });
      setViews((prev) => {
        const next = newViewIsDefault
          ? prev.map((v) => ({ ...v, is_default: false }))
          : prev.slice();
        return [created, ...next];
      });
      setNewViewName("");
      setNewViewIsDefault(false);
      setShowForm(false);
      toast.success(`Saved view "${created.name}".`);
    } catch (err) {
      toast.fromError(err, "We could not save this view.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDefault = async (view, e) => {
    if (e) e.stopPropagation();
    const nextDefault = !view.is_default;
    try {
      const updated = await updateSavedFilter(projectId, view.id, {
        is_default: nextDefault,
      });
      setViews((prev) =>
        prev.map((v) => {
          if (v.id === updated.id) return updated;
          if (nextDefault && v.is_default) return { ...v, is_default: false };
          return v;
        }),
      );
      toast.success(
        nextDefault
          ? `"${view.name}" is now your default view.`
          : `"${view.name}" is no longer the default.`,
      );
    } catch (err) {
      toast.fromError(err, "We could not update this view.");
    }
  };

  const handleDelete = async (view, e) => {
    if (e) e.stopPropagation();
    const ok = await confirm({
      title: "Delete saved view?",
      message: `"${view.name}" will be removed from your saved views.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    try {
      await deleteSavedFilter(projectId, view.id);
      setViews((prev) => prev.filter((v) => v.id !== view.id));
      toast.success(`Deleted "${view.name}".`);
    } catch (err) {
      toast.fromError(err, "We could not delete this view.");
    }
  };
  
  const advisorLabelFor = (advisorId) => {
    if (!advisorId || !project?.advisors?.length) return null;
    const advisors = project.advisors;
    const byId = advisors.find(
      (a) => a.user_id === advisorId || a.id === advisorId,
    );
    const advisor =
      byId || (/^\d+$/.test(String(advisorId)) ? advisors[parseInt(advisorId, 10)] : null);
    if (!advisor) return null;
    return (
      `${advisor.first_name || ""} ${advisor.last_name || ""}`.trim() ||
      advisor.first_name ||
      advisor.last_name ||
      null
    );
  };

  const summarize = (filters = {}) => {
    const parts = [];
    if (filters.status) parts.push(labelForStatus(filters.status));
    const advisorLabel = advisorLabelFor(filters.advisor_id);
    if (advisorLabel) parts.push(`Advisor: ${advisorLabel}`);
    if (filters.q) parts.push(`"${filters.q}"`);
    if (parts.length === 0) parts.push("All households");
    return parts.join(" · ");
  };

  const SaveButton = (
    <button
      type="button"
      onClick={() => {
        setShowForm((s) => !s);
        setNewViewName("");
        setNewViewIsDefault(false);
      }}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 whitespace-nowrap border transition-colors ${
        showForm
          ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
          : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
      }`}
    >
      {showForm ? <X className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />}
      {showForm ? "Cancel" : "Save view"}
    </button>
  );

  return (
    <div className="px-2 mt-2">
      {/* One-line strip: label + chips (or empty hint) + save action */}
      <div className="flex items-center gap-3 min-h-[28px]">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
          Saved Views
        </div>

        {loading ? (
          <span className="text-xs text-gray-400">Loading…</span>
        ) : views.length === 0 ? (
          <span className="text-xs text-gray-400">No saved views yet</span>
        ) : (
          <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 flex-1 min-w-0">
            {views.map((view) => {
              const isActive = view.id === activeViewId;
              return (
                <div
                  key={view.id}
                  className={`inline-flex items-center gap-1 pl-1.5 pr-1 py-1 rounded-full border text-xs transition-colors flex-shrink-0 ${
                    isActive
                      ? "border-blue-500 bg-blue-50 text-blue-800"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                  title={summarize(view.filters)}
                >
                  <button
                    type="button"
                    onClick={(e) => handleToggleDefault(view, e)}
                    title={view.is_default ? "Default view" : "Set as default"}
                    aria-label={view.is_default ? "Default view" : "Set as default"}
                    className={`flex items-center justify-center w-4 h-4 rounded-full hover:bg-gray-100 ${
                      view.is_default ? "text-amber-500" : "text-gray-300"
                    }`}
                  >
                    <Star
                      className="w-3 h-3"
                      fill={view.is_default ? "currentColor" : "none"}
                      strokeWidth={view.is_default ? 0 : 1.5}
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => onApplyView(view)}
                    className="font-medium truncate max-w-[140px] focus:outline-none"
                  >
                    {view.name}
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleDelete(view, e)}
                    aria-label={`Delete saved view "${view.name}"`}
                    title="Delete view"
                    className="flex items-center justify-center w-4 h-4 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="ml-auto">{SaveButton}</div>
      </div>

      {/* Inline "Save current" form — only when toggled open */}
      {showForm && (
        <div className="flex flex-wrap items-center gap-2 mt-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
          <input
            type="text"
            maxLength={100}
            value={newViewName}
            onChange={(e) => setNewViewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveCurrent();
              if (e.key === "Escape") setShowForm(false);
            }}
            placeholder='Name this view (e.g. "NIGO open")'
            className="input-modern text-sm flex-1 min-w-[200px] h-[34px]"
            autoFocus
          />
          <label className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={newViewIsDefault}
              onChange={(e) => setNewViewIsDefault(e.target.checked)}
            />
            Set as default
          </label>
          <button
            type="button"
            onClick={handleSaveCurrent}
            disabled={submitting}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold py-1.5 px-3 rounded-md h-[34px]"
          >
            {submitting ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SavedFiltersPanel;
